// Wikipedia-sourced celebrity reference photos per Kibbe body type.
// Each entry: real Wikipedia thumbnail + canonical page URL, fetched via the
// Wikipedia REST API (en.wikipedia.org/api/rest_v1/page/summary/<Page_Name>).

export interface KibbeCelebrity {
  name: string;
  caption: string;
  imageUrl: string;
  wikipediaUrl: string;
}

export const kibbeCelebrities: Record<string, KibbeCelebrity[]> = {
  "dramatic": [
    {
      name: "Tilda Swinton",
      caption: "Sharp angular bone structure on a long, narrow vertical line.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Tilda_Swinton_at_Cannes_Film_Festival_02_%28cropped%29.jpg/330px-Tilda_Swinton_at_Cannes_Film_Festival_02_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Tilda_Swinton",
    },
    {
      name: "Cate Blanchett",
      caption: "Statuesque frame with sharp cheekbones and minimal soft curve.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Cate_Blanchett-63298_%28cropped_2%29.jpg/330px-Cate_Blanchett-63298_%28cropped_2%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Cate_Blanchett",
    },
    {
      name: "Taylor Swift",
      caption: "Long limbs and narrow, angular shoulders read Dramatic instantly.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/Taylor_Swift_at_the_2023_MTV_Video_Music_Awards_%283%29.png/330px-Taylor_Swift_at_the_2023_MTV_Video_Music_Awards_%283%29.png",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Taylor_Swift",
    },
    {
      name: "Keira Knightley",
      caption: "Defined jawline over a lean, straight-edged frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/Knightley_Bafta_2015_07.png/330px-Knightley_Bafta_2015_07.png",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Keira_Knightley",
    },
    {
      name: "Jamie Lee Curtis",
      caption: "Strong vertical line with angular, unrounded bone structure.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/2025_Jamie_Lee_Curtis_%28cropped%29.jpg/330px-2025_Jamie_Lee_Curtis_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jamie_Lee_Curtis",
    },
  ],
  "soft-dramatic": [
    {
      name: "Sofia Vergara",
      caption: "Bold vertical line paired with full, dramatic curves.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Sof%C3%ADa_Vergara_2019_by_Glenn_Francis.jpg/330px-Sof%C3%ADa_Vergara_2019_by_Glenn_Francis.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Sof%C3%ADa_Vergara",
    },
    {
      name: "Adele",
      caption: "Tall frame carrying lush curves along a long line.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Adele_2016.jpg/330px-Adele_2016.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Adele",
    },
    {
      name: "Christina Hendricks",
      caption: "Sharp bone structure softened by a full, curved figure.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/26/Christina_Hendricks_at_PaleyFest_2014.jpg/330px-Christina_Hendricks_at_PaleyFest_2014.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Christina_Hendricks",
    },
    {
      name: "Sophia Loren",
      caption: "Tall and curvy with dramatic, high-contrast features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/Gala_de_%C3%8Enchidere_TIFF_2016_%2827490660976%29_%28cropped%29.jpg/330px-Gala_de_%C3%8Enchidere_TIFF_2016_%2827490660976%29_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Sophia_Loren",
    },
    {
      name: "Ashley Graham",
      caption: "Vertical height paired with bold, confident curves.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Ashley_Graham_ATTN_2024.jpg/330px-Ashley_Graham_ATTN_2024.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Ashley_Graham_(model)",
    },
  ],
  "flamboyant-natural": [
    {
      name: "Jennifer Lawrence",
      caption: "Athletic frame with blunt, broad-shouldered bone structure.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/Jennifer_Lawrence_in_2018.png/330px-Jennifer_Lawrence_in_2018.png",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Lawrence",
    },
    {
      name: "Cameron Diaz",
      caption: "Broad shoulders on an athletic frame, low on curve.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Cameron_Diaz_at_WWD_Style_Awards_2026-1998.jpg/330px-Cameron_Diaz_at_WWD_Style_Awards_2026-1998.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Cameron_Diaz",
    },
    {
      name: "Julia Roberts",
      caption: "Tall, athletic build with wide shoulders and a wide smile.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Julia_Roberts_2025.jpg/330px-Julia_Roberts_2025.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Julia_Roberts",
    },
    {
      name: "Princess Diana",
      caption: "Tall with broad shoulders and an unfussy, athletic build.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Diana%2C_Princess_of_Wales_1997_%282%29.jpg/330px-Diana%2C_Princess_of_Wales_1997_%282%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Diana%2C_Princess_of_Wales",
    },
    {
      name: "Blake Lively",
      caption: "Tall frame, broad shoulders, long straight limbs.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/Guests_at_the_2026_Met_Gala_435_%28Blake_Lively%29.jpg/330px-Guests_at_the_2026_Met_Gala_435_%28Blake_Lively%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Blake_Lively",
    },
    {
      name: "Anne Hathaway",
      caption: "Long limbs and width through the shoulder line.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/da/Anne_Hathaway-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194764955_%28cropped%29.jpg/330px-Anne_Hathaway-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194764955_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Anne_Hathaway",
    },
  ],
  "natural": [
    {
      name: "Jennifer Aniston",
      caption: "Sturdy, moderate frame with relaxed, blunt features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/JenniferAnistonHWoFFeb2012.jpg/330px-JenniferAnistonHWoFFeb2012.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Aniston",
    },
    {
      name: "Meg Ryan",
      caption: "Not extreme in any direction — an easy, moderate build.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Meg_Ryan_2_Met_Opera_2010_Shankbone.jpg/330px-Meg_Ryan_2_Met_Opera_2010_Shankbone.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Meg_Ryan",
    },
    {
      name: "Charlize Theron",
      caption: "Tall, athletic frame with broad shoulders and long limbs.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/CharlizeTheron-byPhilipRomano.jpg/330px-CharlizeTheron-byPhilipRomano.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Charlize_Theron",
    },
    {
      name: "Lauren Bacall",
      caption: "Angular but moderate frame, never sharp to an extreme.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/Lauren_Bacall_1945_press_photo.jpg/330px-Lauren_Bacall_1945_press_photo.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Lauren_Bacall",
    },
    {
      name: "Brooke Shields",
      caption: "Strong, athletic bone structure with an approachable build.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Brooke_Shields_%282008%29_cropped.JPG/330px-Brooke_Shields_%282008%29_cropped.JPG",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Brooke_Shields",
    },
    {
      name: "Diane Keaton",
      caption: "Angular features on a relaxed, unconstructed frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Diane_Keaton_2012-1_%28cropped%29_%28cropped%29.jpg/330px-Diane_Keaton_2012-1_%28cropped%29_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Diane_Keaton",
    },
  ],
  "soft-natural": [
    {
      name: "Scarlett Johansson",
      caption: "Width through the frame layered with soft curves.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/Scarlett_Johansson-8588.jpg/330px-Scarlett_Johansson-8588.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Scarlett_Johansson",
    },
    {
      name: "Gigi Hadid",
      caption: "Athletic width softened by a rounded curve line.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Marina_Ruy_Barbosa_com_Gigi_Hadid_em_Paris_%28Gigi_Hadid%29.jpg/330px-Marina_Ruy_Barbosa_com_Gigi_Hadid_em_Paris_%28Gigi_Hadid%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Gigi_Hadid",
    },
    {
      name: "Jennifer Lopez",
      caption: "Width and curve together across hip and shoulder.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Jennifer_Lopez_at_the_2025_Sundance_Film_Festival_%28cropped_3%29.jpg/330px-Jennifer_Lopez_at_the_2025_Sundance_Film_Festival_%28cropped_3%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Lopez",
    },
    {
      name: "Kate Winslet",
      caption: "Soft curves on a broad, sturdy frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/KateWinslet_%28cropped%29.jpg/330px-KateWinslet_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Kate_Winslet",
    },
    {
      name: "Sydney Sweeney",
      caption: "Soft curves layered over a sturdy, wide frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f8/Sydney_Sweeney_at_the_2024_Toronto_International_Film_Festival_01_%283x4_cropped_and_rotated%29.jpg/330px-Sydney_Sweeney_at_the_2024_Toronto_International_Film_Festival_01_%283x4_cropped_and_rotated%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Sydney_Sweeney",
    },
  ],
  "dramatic-classic": [
    {
      name: "Jackie Kennedy",
      caption: "Refined, structured proportions with polished symmetry.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Jackie_Kennedy_Color_Portrait_%283x4_cropped%29.jpg/330px-Jackie_Kennedy_Color_Portrait_%283x4_cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jacqueline_Kennedy_Onassis",
    },
    {
      name: "Olivia Palermo",
      caption: "Sharp features balanced by moderate, even proportions.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cb/Oliva_Palermo_-_Flickr_-_nick_step.jpg/330px-Oliva_Palermo_-_Flickr_-_nick_step.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Olivia_Palermo",
    },
    {
      name: "Claire Danes",
      caption: "Moderate frame carrying sharp, defined features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Claire_Danes_in_2015.jpg/330px-Claire_Danes_in_2015.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Claire_Danes",
    },
    {
      name: "Diane Sawyer",
      caption: "Commanding presence on a balanced, moderate build.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Diane_Sawyer_at_the_2025_Toronto_International_Film_Festival_%28cropped%29.jpg/330px-Diane_Sawyer_at_the_2025_Toronto_International_Film_Festival_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Diane_Sawyer",
    },
    {
      name: "Jane Fonda",
      caption: "Balanced proportions with a dramatic edge to the features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/20/Jane_Fonda_1963.jpg/330px-Jane_Fonda_1963.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jane_Fonda",
    },
    {
      name: "Demi Moore",
      caption: "Balanced frame with sharper, more dramatic undertones.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Demi_Moore_at_the_2026_Cannes_Film_Festival_%28cropped%29.jpg/330px-Demi_Moore_at_the_2026_Cannes_Film_Festival_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Demi_Moore",
    },
  ],
  "classic": [
    {
      name: "Grace Kelly",
      caption: "Evenly balanced features and proportions, no dominant line.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Grace_Kelly_1955.jpg/330px-Grace_Kelly_1955.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Grace_Kelly",
    },
    {
      name: "Meryl Streep",
      caption: "Balanced bone structure that reads even in every angle.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Meryl_Streep-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194765350_%28cropped1%29.jpg/330px-Meryl_Streep-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194765350_%28cropped1%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Meryl_Streep",
    },
    {
      name: "Kate Middleton",
      caption: "Balanced proportions with understated, symmetrical features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/Princess_of_Wales_at_State_Banquet_2025-09-17_%280.75_crop%29.jpg/330px-Princess_of_Wales_at_State_Banquet_2025-09-17_%280.75_crop%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Catherine%2C_Princess_of_Wales",
    },
    {
      name: "Carolina Herrera",
      caption: "Moderate, symmetrical proportions with refined polish.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Carolina_Herrera_alt.jpg/330px-Carolina_Herrera_alt.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Carolina_Herrera",
    },
    {
      name: "Diane Lane",
      caption: "Symmetrical features on a moderate, balanced frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5f/Diane_Lane_%28Berlin_Film_Festival_2011%29_2.jpg/330px-Diane_Lane_%28Berlin_Film_Festival_2011%29_2.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Diane_Lane",
    },
    {
      name: "Emma Watson",
      caption: "Moderate proportions with refined, even features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7f/Emma_Watson_2013.jpg/330px-Emma_Watson_2013.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Emma_Watson",
    },
  ],
  "soft-classic": [
    {
      name: "Naomi Watts",
      caption: "Moderate frame softened by gentle, rounded curves.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/55/Naomi_Watts_at_the_2024_New_York_Film_Festival_2_%28cropped%29.jpg/330px-Naomi_Watts_at_the_2024_New_York_Film_Festival_2_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Naomi_Watts",
    },
    {
      name: "Marion Cotillard",
      caption: "Balanced bone structure with soft, rounded edges.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/Marion_Cotillard_at_2019_Cannes.jpg/330px-Marion_Cotillard_at_2019_Cannes.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Marion_Cotillard",
    },
    {
      name: "Dakota Johnson",
      caption: "Balanced proportions with understated softness at the edges.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c2/Dakota_Johnson_at_the_2025_Cannes_Film_Festival_%28cropped%29.jpg/330px-Dakota_Johnson_at_the_2025_Cannes_Film_Festival_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Dakota_Johnson",
    },
    {
      name: "Lee Remick",
      caption: "Balanced frame with a feminine, softened finish.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c0/Lee_Remick_4_Allan_Warren.jpg/330px-Lee_Remick_4_Allan_Warren.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Lee_Remick",
    },
    {
      name: "Leighton Meester",
      caption: "Balanced features softened by a gentle yin quality.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/Leighton_Meester_2017.jpg/330px-Leighton_Meester_2017.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Leighton_Meester",
    },
    {
      name: "Rose Byrne",
      caption: "Moderate frame with soft, rounded facial features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/Rose_Byrne_at_Berlinale_2025-1_%28cropped%29.jpg/330px-Rose_Byrne_at_Berlinale_2025-1_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Rose_Byrne",
    },
  ],
  "flamboyant-gamine": [
    {
      name: "Audrey Hepburn",
      caption: "Petite frame mixing sharp collarbones with delicate features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/AudreyKHepburn.jpg/330px-AudreyKHepburn.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Audrey_Hepburn",
    },
    {
      name: "Twiggy",
      caption: "Boyish, narrow frame with striking, oversized eyes.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Twiggy_-_GylesDamesPalladium050323_%2819_of_74%29_%2852728638973%29.jpg/330px-Twiggy_-_GylesDamesPalladium050323_%2819_of_74%29_%2852728638973%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Twiggy",
    },
    {
      name: "Liza Minnelli",
      caption: "Compact frame carrying sharp, theatrical features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/2/27/Liza_Minnelli.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Liza_Minnelli",
    },
    {
      name: "Lily Collins",
      caption: "Petite frame with bold, high-contrast brows and eyes.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Lily_Collins_May_2019.jpg/330px-Lily_Collins_May_2019.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Lily_Collins",
    },
    {
      name: "Penelope Cruz",
      caption: "Petite frame with sharp, high-contrast features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ad/Goyas_2024_-_Pen%C3%A9lope_Cruz-2_%28cropped%29.jpg/330px-Goyas_2024_-_Pen%C3%A9lope_Cruz-2_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Pen%C3%A9lope_Cruz",
    },
    {
      name: "Zooey Deschanel",
      caption: "Petite frame with striking eyes and angular brows.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/Zooey_Deschanel_-_Song_Sung_Blue.jpg/330px-Zooey_Deschanel_-_Song_Sung_Blue.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Zooey_Deschanel",
    },
  ],
  "gamine": [
    {
      name: "Mia Farrow",
      caption: "Delicate, pixie-scale frame with wide, youthful eyes.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Pulitzer2018-mia-farrow-20180530-wp.jpg/330px-Pulitzer2018-mia-farrow-20180530-wp.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Mia_Farrow",
    },
    {
      name: "Jean Seberg",
      caption: "Androgynous, boyish frame with a cropped, angular finish.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Jean_seberg_panorama_1963.jpg/330px-Jean_seberg_panorama_1963.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jean_Seberg",
    },
    {
      name: "Carey Mulligan",
      caption: "Delicate features on a compact, small-scale frame.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Carey_Mulligan_at_the_2025_Sundance_Film_Festival_%28cropped%29.jpg/330px-Carey_Mulligan_at_the_2025_Sundance_Film_Festival_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Carey_Mulligan",
    },
    {
      name: "Vanessa Paradis",
      caption: "Elfin frame with wide-set, youthful features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Vanessa_Paradis_Cannes_2016_2.jpg/330px-Vanessa_Paradis_Cannes_2016_2.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Vanessa_Paradis",
    },
    {
      name: "Edie Sedgwick",
      caption: "Androgynous, narrow frame with oversized, dark eyes.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/3/3a/Edie_Sedgwick_smoking_a_cigarette.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Edie_Sedgwick",
    },
    {
      name: "Leslie Caron",
      caption: "Petite frame mixing sharp cheekbones with soft, round eyes.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Leslie_Caron-publicity.JPG/330px-Leslie_Caron-publicity.JPG",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Leslie_Caron",
    },
  ],
  "soft-gamine": [
    {
      name: "Reese Witherspoon",
      caption: "Petite frame with soft curves and rounded features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Reese_Witherspoon_%2829144489784%29_%28cropped%29.jpg/330px-Reese_Witherspoon_%2829144489784%29_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Reese_Witherspoon",
    },
    {
      name: "Winona Ryder",
      caption: "Compact frame with soft, doll-like features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ee/Winona_Ryder-63849.jpg/330px-Winona_Ryder-63849.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Winona_Ryder",
    },
    {
      name: "Sarah Jessica Parker",
      caption: "Petite frame with soft curves and playful proportions.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Sarah_Jessica_Parker_2022.jpg/330px-Sarah_Jessica_Parker_2022.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Sarah_Jessica_Parker",
    },
    {
      name: "Jenna Coleman",
      caption: "Small-scale frame with soft, rounded features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Jenna_Coleman_at_Gallifrey_One_2025.jpg/330px-Jenna_Coleman_at_Gallifrey_One_2025.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jenna_Coleman",
    },
    {
      name: "Lucy Hale",
      caption: "Petite frame with soft curves and youthful proportions.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Lucy_Hale_in_2018.jpg/330px-Lucy_Hale_in_2018.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Lucy_Hale",
    },
  ],
  "theatrical-romantic": [
    {
      name: "Vivien Leigh",
      caption: "Petite, curvy frame with sharp, high-contrast features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b6/Vivien_Leigh_Scarlet.jpg/330px-Vivien_Leigh_Scarlet.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Vivien_Leigh",
    },
    {
      name: "Salma Hayek",
      caption: "Petite and curvy with striking, dramatic bone structure.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/MKr383631_Salma_Hayek_%28Women_In_Motion%2C_Cannes_2025%29_crop.jpg/330px-MKr383631_Salma_Hayek_%28Women_In_Motion%2C_Cannes_2025%29_crop.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Salma_Hayek",
    },
    {
      name: "Mila Kunis",
      caption: "Petite frame with curves and sharp facial contrast.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Mila_Kunis_TIFF_2025_%28cropped%29.jpg/330px-Mila_Kunis_TIFF_2025_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Mila_Kunis",
    },
    {
      name: "Eva Longoria",
      caption: "Petite curves paired with strong, defined features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f8/Eva_Longoria-1384.jpg/330px-Eva_Longoria-1384.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Eva_Longoria",
    },
    {
      name: "Rachel McAdams",
      caption: "Soft curves paired with sharper, defined features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Rachel_McAdams_-_Walk_of_Fame.jpg/330px-Rachel_McAdams_-_Walk_of_Fame.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Rachel_McAdams",
    },
    {
      name: "Jada Pinkett Smith",
      caption: "Petite, curvy frame with dramatic bone structure.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/Jada_pinkett_smith_2017_%28cropped%29.png/330px-Jada_pinkett_smith_2017_%28cropped%29.png",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Jada_Pinkett_Smith",
    },
  ],
  "romantic": [
    {
      name: "Marilyn Monroe",
      caption: "Soft, rounded curves with no sharp lines anywhere.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Monroecirca1953.jpg/330px-Monroecirca1953.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Marilyn_Monroe",
    },
    {
      name: "Drew Barrymore",
      caption: "Soft, rounded figure that reads best in flowing drape.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/Drew_Barrymore_in_2019_%28cropped%29.jpg/330px-Drew_Barrymore_in_2019_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Drew_Barrymore",
    },
    {
      name: "Christina Ricci",
      caption: "Petite frame with soft, rounded, doll-like features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/Christina_Ricci_%2849596830836%29_%28cropped%29.jpg/330px-Christina_Ricci_%2849596830836%29_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Christina_Ricci",
    },
    {
      name: "Emilia Clarke",
      caption: "Small, delicate frame with soft, rounded features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Emilia_Clarke_at_the_2023_Harper%27s_Bazaar_Women_of_the_Year_Awards.jpg/330px-Emilia_Clarke_at_the_2023_Harper%27s_Bazaar_Women_of_the_Year_Awards.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Emilia_Clarke",
    },
    {
      name: "Helena Bonham Carter",
      caption: "Soft, rounded bone structure beneath an eclectic style.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/MerchantIvoryCurzMayfair201124_%2816_of_28%29_%2854154554145%29_%28cropped%29.jpg/330px-MerchantIvoryCurzMayfair201124_%2816_of_28%29_%2854154554145%29_%28cropped%29.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Helena_Bonham_Carter",
    },
    {
      name: "Elizabeth Taylor",
      caption: "Curved figure with soft, rounded facial features.",
      imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Taylor%2C_Elizabeth_posed.jpg/330px-Taylor%2C_Elizabeth_posed.jpg",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Elizabeth_Taylor",
    },
  ],
};
